import axios from 'axios';
import { BACKEND_URL } from './config';
import { clearSession, getToken } from './auth';

const client = axios.create({ baseURL: `${BACKEND_URL}/api` });

client.interceptors.request.use((cfg) => {
  const token = getToken();
  if (token) cfg.headers.Authorization = `Bearer ${token}`;
  return cfg;
});

// s159: trial / membership over → the brand home shows the pay modal
// (lmsbeta.aurexnetwork.com → beta.aurexnetwork.com, lms.carlosartiles.com → carlosartiles.com)
const brandHome = () => {
  const parts = window.location.hostname.split('.');
  const first = parts.shift();
  return `${window.location.protocol}//${/beta$/.test(first) ? 'beta.' : ''}${parts.join('.')}/?membership=expired`;
};
const membershipLapsed = (err) => err.response?.status === 403 && err.response?.data?.detail?.code === 'membership_lapsed';

client.interceptors.response.use(
  (res) => res,
  (err) => {
    if (membershipLapsed(err)) { window.location.assign(brandHome()); return Promise.reject(err); }
    if (err.response?.status === 401 && window.location.pathname !== '/login') {
      clearSession();
      window.location.assign('/login');
    }
    return Promise.reject(err);
  }
);

export const authAPI = {
  login: (username, password) => client.post('/auth/login', { username, password }),
  ssoLogin: (token) => client.get('/auth/sso-login', { params: { token } }),
  me: () => client.get('/auth/me'),
  logout: () => client.post('/auth/logout'),
};

export const healthAPI = {
  check: () => client.get('/health'),
};

export const brandAPI = {
  get: () => client.get('/public/brand'),
};

export const opsAPI = {
  morningBriefStatus: () => client.get('/ops/morning-brief-status'),
};

export const sharesAPI = {
  contentSearch: (q) => client.get('/shares/content-search', { params: { q } }),
  setShareItem: (taskId, data) => client.put(`/tasks/${taskId}/share-item`, data),
  taskShares: (taskId) => client.get(`/tasks/${taskId}/shares`),
  click: (taskId, network) => client.post(`/tasks/${taskId}/share-click`, { network }),
};

export const projectsAPI = {
  list: (archived = false) => client.get('/projects', { params: { archived } }),
  create: (data) => client.post('/projects', data),
  get: (id) => client.get(`/projects/${id}`),
  update: (id, data) => client.put(`/projects/${id}`, data),
  remove: (id) => client.delete(`/projects/${id}`),
  archive: (id) => client.post(`/projects/${id}/archive`),
  unarchive: (id) => client.post(`/projects/${id}/unarchive`),
  favorite: (id) => client.post(`/projects/${id}/favorite`),
  members: (id) => client.get(`/projects/${id}/members`),
  setMembers: (id, data) => client.put(`/projects/${id}/members`, data),
  activity: (id, before = '') => client.get(`/projects/${id}/activity`, { params: { before } }),
  folders: () => client.get('/folders'),
  createFolder: (data) => client.post('/folders', data),
  roster: () => client.get('/members'),
};

export const tasksAPI = {
  groups: (projectId) => client.get(`/projects/${projectId}/groups`),
  createGroup: (projectId, data) => client.post(`/projects/${projectId}/groups`, data),
  updateGroup: (id, data) => client.put(`/groups/${id}`, data),
  deleteGroup: (id) => client.delete(`/groups/${id}`),
  reorderGroups: (projectId, ids) => client.put(`/projects/${projectId}/groups/reorder`, { ids }),
  list: (projectId, params = {}) => client.get(`/projects/${projectId}/tasks`, { params }),
  create: (projectId, data) => client.post(`/projects/${projectId}/tasks`, data),
  get: (id) => client.get(`/tasks/${id}`),
  patch: (id, data) => client.put(`/tasks/${id}`, data),
  remove: (id) => client.delete(`/tasks/${id}`),
  move: (id, groupId, order) => client.put(`/tasks/${id}/move`, { group_id: groupId, order }),
  complete: (id) => client.post(`/tasks/${id}/complete`),
  reopen: (id) => client.post(`/tasks/${id}/reopen`),
  comments: (id) => client.get(`/tasks/${id}/comments`),
  addComment: (id, data) => client.post(`/tasks/${id}/comments`, data),
  deleteComment: (id) => client.delete(`/comments/${id}`),
  labels: (projectId) => client.get(`/projects/${projectId}/labels`),
  createLabel: (projectId, data) => client.post(`/projects/${projectId}/labels`, data),
  updateLabel: (id, data) => client.put(`/labels/${id}`, data),
  deleteLabel: (id) => client.delete(`/labels/${id}`),
  bulk: (ids, action, extra = {}) => client.put('/tasks/bulk', { ids, action, ...extra }),
  customFields: (projectId) => client.get(`/projects/${projectId}/custom-fields`),
  createCustomField: (projectId, data) => client.post(`/projects/${projectId}/custom-fields`, data),
  updateCustomField: (id, data) => client.put(`/custom-fields/${id}`, data),
  deleteCustomField: (id) => client.delete(`/custom-fields/${id}`),
};

export const milestonesAPI = {
  list: (projectId) => client.get(`/projects/${projectId}/milestones`),
  create: (projectId, data) => client.post(`/projects/${projectId}/milestones`, data),
  update: (id, data) => client.put(`/milestones/${id}`, data),
  remove: (id) => client.delete(`/milestones/${id}`),
  roadmap: () => client.get('/overview/roadmap'),
};

export const meAPI = {
  tasks: () => client.get('/me/tasks'),
  calendar: (start, end) => client.get('/me/calendar', { params: { start, end } }),
  taskDetail: (taskId) => client.get(`/me/tasks/${taskId}`),
  points: () => client.get('/me/points'),
};

export const chatAPI = {
  channels: () => client.get('/channels'),
  messages: (channelId, before = '') => client.get(`/channels/${channelId}/messages`, { params: { before } }),
  send: (channelId, data) => client.post(`/channels/${channelId}/messages`, data),
  edit: (messageId, data) => client.put(`/messages/${messageId}`, data),
  remove: (messageId) => client.delete(`/messages/${messageId}`),
  react: (messageId, emoji) => client.post(`/messages/${messageId}/react`, { emoji }),
  convert: (messageId, groupId) => client.post(`/messages/${messageId}/convert`, { group_id: groupId }),
  markRead: (channelId) => client.put(`/channels/${channelId}/read`),
};

export const docsAPI = {
  list: (projectId) => client.get(`/projects/${projectId}/docs`),
  create: (projectId, data) => client.post(`/projects/${projectId}/docs`, data),
  get: (id) => client.get(`/docs/${id}`),
  put: (id, data) => client.put(`/docs/${id}`, data),
  remove: (id) => client.delete(`/docs/${id}`),
  share: (id) => client.post(`/docs/${id}/share`),
  public: (token) => client.get(`/public/docs/${token}`),
};

export const filesAPI = {
  list: (projectId) => client.get(`/projects/${projectId}/files`),
  upload: (projectId, file, folderPath = '', onProgress) => {
    const form = new FormData();
    form.append('file', file);
    return client.post(`/projects/${projectId}/files?folder_path=${encodeURIComponent(folderPath)}`, form, {
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress: onProgress,
    });
  },
  downloadUrl: (id) => `${BACKEND_URL}/api/files/${id}/download`,
  remove: (id) => client.delete(`/files/${id}`),
  share: (id) => client.post(`/files/${id}/share`),
};

export const timeAPI = {
  start: (taskId) => client.post(`/tasks/${taskId}/timer/start`),
  stop: (taskId) => client.post(`/tasks/${taskId}/timer/stop`),
  running: () => client.get('/me/timer'),
  taskTime: (taskId) => client.get(`/tasks/${taskId}/time`),
  entries: (params = {}) => client.get('/time-entries', { params }),
  createManual: (data) => client.post('/time-entries', data),
  patchEntry: (id, data) => client.put(`/time-entries/${id}`, data),
  deleteEntry: (id) => client.delete(`/time-entries/${id}`),
  report: (params = {}) => client.get('/reports/time', { params }),
  csv: (params = {}) => client.get('/reports/time', { params: { ...params, format: 'csv' }, responseType: 'blob' }),
};

export const reportsAPI = {
  workloads: (start = '', end = '') => client.get('/workloads', { params: { start, end } }),
  project: (id) => client.get(`/reports/project/${id}`),
  workspace: () => client.get('/reports/workspace'),
};

export const formsAPI = {
  list: (projectId) => client.get(`/projects/${projectId}/forms`),
  create: (projectId, data) => client.post(`/projects/${projectId}/forms`, data),
  update: (id, data) => client.put(`/forms/${id}`, data),
  remove: (id) => client.delete(`/forms/${id}`),
  publicGet: (slug) => client.get(`/public/forms/${slug}`),
  publicSubmit: (slug, data) => client.post(`/public/forms/${slug}/submit`, data),
};

export const checkinsAPI = {
  list: () => client.get('/checkins'),
  create: (data) => client.post('/checkins', data),
  update: (id, data) => client.put(`/checkins/${id}`, data),
  remove: (id) => client.delete(`/checkins/${id}`),
  respond: (id, answers) => client.post(`/checkins/${id}/respond`, { answers }),
  digest: (id, date = '') => client.get(`/checkins/${id}/digest`, { params: { date } }),
};

export const templatesAPI = {
  list: () => client.get('/templates'),
  saveAs: (projectId, name) => client.post(`/projects/${projectId}/save-as-template`, { name }),
  remove: (id) => client.delete(`/templates/${id}`),
  instantiate: (id, name) => client.post(`/templates/${id}/instantiate`, { name }),
  importCsv: (projectId, data) => client.post(`/projects/${projectId}/import/csv`, data),
};

export const notifyAPI = {
  list: (unread = false) => client.get('/notifications', { params: { unread } }),
  badge: () => client.get('/notifications/badge'),
  markRead: (ids = null) => client.put('/notifications/read', { ids }),
};

export default client;
